import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, CalendarDays, Timer, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const REPORTS = {
  late: {
    label: "Late Attendance",
    icon: Clock,
    endpoint: "late-attendance",
    columns: [
      { key: "day", label: "Date", date: true },
      { key: "name", label: "Employee" },
      { key: "staff_id", label: "Staff ID" },
      { key: "site_name", label: "Site" },
      { key: "shift_start", label: "Shift Start" },
      { key: "clock_in", label: "Clock In", time: true },
      { key: "minutes_late", label: "Minutes Late" },
    ],
  },
  attendance: {
    label: "Attendance Report",
    icon: CalendarDays,
    endpoint: "attendance",
    columns: [
      { key: "day", label: "Date", date: true },
      { key: "name", label: "Employee" },
      { key: "staff_id", label: "Staff ID" },
      { key: "site_name", label: "Site" },
      { key: "clock_in", label: "Clock In", time: true },
      { key: "clock_out", label: "Clock Out", time: true },
      { key: "status", label: "Status" },
    ],
  },
  overtime: {
    label: "Overtime Report",
    icon: Timer,
    endpoint: "overtime",
    columns: [
      { key: "day", label: "Date", date: true },
      { key: "name", label: "Employee" },
      { key: "staff_id", label: "Staff ID" },
      { key: "site_name", label: "Site" },
      { key: "hours", label: "Hours" },
      { key: "reason", label: "Reason" },
      { key: "approved_by", label: "Approved By" },
    ],
  },
};

const ddmmyyyy = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const hhmm = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtCell = (col, val) => (col.date ? ddmmyyyy(val) : col.time ? hhmm(val) : val == null || val === "" ? "—" : String(val));

// Preload logo as data URL for PDF embedding
let LOGO_CACHE = null;
async function getLogoDataUrl() {
  if (LOGO_CACHE) return LOGO_CACHE;
  try {
    const res = await fetch("/logo-lg.webp");
    const blob = await res.blob();
    const img = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d").drawImage(img, 0, 0);
    LOGO_CACHE = canvas.toDataURL("image/png");
  } catch {
    LOGO_CACHE = null;
  }
  return LOGO_CACHE;
}

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10);
  const [tab, setTab] = useState("late");
  const [employeeId, setEmployeeId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [start, setStart] = useState(thirtyAgo);
  const [end, setEnd] = useState(today);
  const [exporting, setExporting] = useState(false);

  const { data: filters } = useQuery({ queryKey: ["report-filters"], queryFn: () => api.get("/admin/reports/filters") });
  const conf = REPORTS[tab];
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (employeeId) p.set("employee_id", employeeId);
    if (siteId) p.set("site_id", siteId);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    return p.toString();
  }, [employeeId, siteId, start, end]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["report", tab, qs],
    queryFn: () => api.get(`/admin/reports/${conf.endpoint}?${qs}`),
  });

  const subtitle = () => {
    const parts = [`${ddmmyyyy(start)} – ${ddmmyyyy(end)}`];
    if (employeeId && filters) parts.push(filters.employees.find((e) => e.id === employeeId)?.name || "");
    if (siteId && filters) parts.push(filters.sites.find((s) => s.id === siteId)?.name || "");
    return parts.filter(Boolean).join("  •  ");
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const logo = await getLogoDataUrl();
      if (logo) doc.addImage(logo, "PNG", 40, 28, 46, 46);
      doc.setFont("helvetica", "bold").setFontSize(18).setTextColor(15, 23, 42);
      doc.text("Smart-In", logo ? 98 : 40, 46);
      doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(100, 116, 139);
      doc.text(conf.label, logo ? 98 : 40, 63);
      doc.setFontSize(9).setTextColor(148, 163, 184);
      doc.text(subtitle(), logo ? 98 : 40, 77);
      doc.setDrawColor(226, 232, 240);
      doc.line(40, 88, doc.internal.pageSize.getWidth() - 40, 88);
      autoTable(doc, {
        startY: 98,
        head: [conf.columns.map((c) => c.label)],
        body: rows.map((r) => conf.columns.map((c) => fmtCell(c, r[c.key]))),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 40, right: 40 },
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      doc.setFontSize(8).setTextColor(148, 163, 184);
      doc.text(`Generated ${ddmmyyyy(today)} • Smart-In`, 40, ph - 20);
      doc.text(`${rows.length} record(s)`, pw - 40, ph - 20, { align: "right" });
      doc.save(`${conf.label.replace(/\s+/g, "_")}_${today}.pdf`);
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = () => {
    try {
      const aoa = [
        ["Smart-In — " + conf.label],
        [subtitle()],
        [],
        conf.columns.map((c) => c.label),
        ...rows.map((r) => conf.columns.map((c) => fmtCell(c, r[c.key]))),
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = conf.columns.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, conf.label.slice(0, 28));
      XLSX.writeFile(wb, `${conf.label.replace(/\s+/g, "_")}_${today}.xlsx`);
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="reports-page">
      {/* Tabs */}
      <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1" data-testid="report-tabs">
        {Object.entries(REPORTS).map(([key, r]) => {
          const Icon = r.icon;
          return (
            <button key={key} onClick={() => setTab(key)} data-testid={`report-tab-${key}`}
              className={`inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold transition ${tab === key ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>
              <Icon className="h-4 w-4" /> {r.label}
            </button>
          );
        })}
      </div>

      {/* Filters + export */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex flex-col lg:flex-row lg:items-end gap-3">
        <Filter label="Employee">
          <select data-testid="report-filter-employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="report-input">
            <option value="">All employees</option>
            {filters?.employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.staff_id})</option>)}
          </select>
        </Filter>
        <Filter label="Site">
          <select data-testid="report-filter-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="report-input">
            <option value="">All sites</option>
            {filters?.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Filter>
        <Filter label="From">
          <input data-testid="report-filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="report-input" />
        </Filter>
        <Filter label="To">
          <input data-testid="report-filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="report-input" />
        </Filter>
        <div className="flex gap-2 lg:ml-auto">
          <button onClick={exportPDF} disabled={exporting || !rows.length} data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 transition">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </button>
          <button onClick={exportExcel} disabled={!rows.length} data-testid="export-excel-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
        </div>
      </div>

      {/* Report card with logo header */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="report-card">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <img src="/logo.webp" alt="Smart-In" className="h-11 w-11 rounded-xl object-cover bg-white ring-1 ring-slate-200" />
          <div>
            <h2 className="font-head text-lg font-bold text-slate-900 leading-tight">{conf.label}</h2>
            <p className="text-xs text-slate-400">{subtitle()}</p>
          </div>
          <span className="ml-auto text-xs font-semibold text-slate-400" data-testid="report-count">{rows.length} record(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-400 border-b border-slate-100 bg-slate-50/50">
                {conf.columns.map((c) => <th key={c.key} className="px-4 py-3 whitespace-nowrap">{c.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="report-table-body">
              {isLoading ? (
                <tr><td colSpan={conf.columns.length} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={conf.columns.length} className="px-4 py-12 text-center text-slate-400">No records for the selected filters</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50/60 transition">
                    {conf.columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${c.key === "name" ? "font-medium text-slate-800" : "text-slate-600"}`}>
                        {c.key === "status" ? <StatusPill status={r[c.key]} /> : fmtCell(c, r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Filter({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-semibold text-slate-500 uppercase">{label}</label>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    present: "bg-emerald-50 text-emerald-700",
    late: "bg-amber-50 text-amber-700",
    absent: "bg-rose-50 text-rose-700",
    review: "bg-sky-50 text-sky-700",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-600"}`}>{status || "—"}</span>;
}
